import { Avatar, type SxProps, type Theme } from '@mui/material'
import React from 'react'
import { useDigitalFile } from '@/hooks/useDigitalFile'
import type { IDigitalFileSimplified } from '@/modules/DigitalFile/DigitalFile'

const COLOR_PALETTE = [
  '#1976d2',
  '#7b1fa2',
  '#388e3c',
  '#d32f2f',
  '#f57c00',
  '#0288d1',
  '#5d4037',
  '#455a64',
  '#c2185b',
  '#00796b',
  '#512da8',
  '#689f38',
] as const

function hashSeed(input: string): number {
  let h = 0
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

function getInitials(
  firstName?: string | null,
  lastName?: string | null,
  fullName?: string | null,
): string {
  const f = (firstName ?? '').trim().charAt(0)
  const l = (lastName ?? '').trim().charAt(0)
  if (f || l) return `${f}${l}`.toUpperCase()

  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase()
  }
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase()
  }

  return '?'
}

export type TEntityAvatarVariant = 'active' | 'muted' | 'pending'

interface IProps {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  logo?: IDigitalFileSimplified | null
  size?: number
  variant?: TEntityAvatarVariant
  sx?: SxProps<Theme>
}

const EntityAvatarInternal: React.FC<IProps> = ({
  firstName,
  lastName,
  fullName,
  logo,
  size = 36,
  variant = 'active',
  sx,
}) => {
  const file = useDigitalFile(logo ?? undefined)
  const imageUrl = file.data?.fileUrl ?? file.data?.base64

  const seed = `${firstName ?? ''}|${lastName ?? ''}|${fullName ?? ''}`
  const paletteColor = COLOR_PALETTE[hashSeed(seed || '?') % COLOR_PALETTE.length]!
  const initials = getInitials(firstName, lastName, fullName)

  const bgcolor =
    variant === 'pending' ? 'grey.200' : variant === 'muted' ? 'grey.300' : paletteColor
  const color = variant === 'pending' || variant === 'muted' ? 'text.secondary' : 'common.white'

  return (
    <Avatar
      src={imageUrl || undefined}
      alt={fullName ?? (`${firstName ?? ''} ${lastName ?? ''}`.trim() || undefined)}
      sx={[
        {
          width: size,
          height: size,
          bgcolor,
          color,
          fontSize: size <= 24 ? '0.625rem' : size <= 36 ? '0.8rem' : '0.95rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {initials}
    </Avatar>
  )
}

export const EntityAvatar = React.memo(EntityAvatarInternal)
